import BaseApi from './BaseApi.js';

/**
 * 診断レポート送信用APIクライアント
 */
export default class DiagnosisReportApi extends BaseApi {
  /**
   * 新しいDiagnosisReportApiインスタンスを作成
   */
  constructor() {
    super('/api/diagnosis-report');
  }

  /**
   * 診断結果を事務所に送信
   *
   * 診断結果画面のレンダリング直後に自動的に呼び出され、法定相続の結果を送信します。
   * ユーザーには送信の通知は表示されません。
   *
   * 送信される診断結果データ:
   * - 家族構成図（SVG）
   * - 資産入力データ
   * - 診断結果の表（相続人、割合、相続財産、相続税）
   * - 診断日時
   * - 事務所ID
   *
   * @param {Object} data 診断レポートデータ
   *
   * @param {number} data.firmId - 事務所ID（送信先メールアドレス取得に使用）
   *
   * @param {string} data.diagnosisDate - 診断日時（ISO 8601形式）
   * @example "2025-11-23T10:30:00+09:00"
   *
   * @param {string} data.familyTreeSvg - 家族構成図のSVG文字列
   * @example "<svg viewBox='0 0 800 600'>...</svg>"
   *
   * @param {Object} data.assets - 資産入力データ（ユーザーが入力した値そのまま）
   * @param {string} [data.assets.cash] - 現金（万円）
   * @param {string} [data.assets.house_own] - 家屋 - 自宅（万円）
   * @param {string} [data.assets.house_rental] - 家屋 - 貸付事業用（万円）
   * @param {string} [data.assets.house_other] - 家屋 - その他（万円）
   * @param {string} [data.assets.land_business] - 土地 - 自社事業用（万円）
   * @param {string} [data.assets.land_rental] - 土地 - 貸付事業用（万円）
   * @param {string} [data.assets.land_other] - 土地 - その他（万円）
   * @param {string} [data.assets.securities_company] - 有価証券 - 自社株（万円）
   * @param {string} [data.assets.securities_public] - 有価証券 - 上場株（万円）
   * @param {string} [data.assets.securities_trust] - 有価証券 - 投資信託（万円）
   * @param {string} [data.assets.securities_other] - 有価証券 - その他（万円）
   * @param {string} [data.assets.household] - 家庭用財産（万円）
   * @param {string} [data.assets.insurance] - 保険金（万円）
   * @param {string} [data.assets.retirement] - 退職金（万円）
   * @param {string} [data.assets.other] - その他（万円）
   * @param {string} [data.assets.gift] - 3年以内の贈与（万円）
   * @param {string} [data.assets.debt] - 借金（万円）
   *
   * @param {Object} data.result - 診断結果データ
   * @param {number} data.result.totalAssets - 総資産額（単位: 万円）
   * @example 10000
   *
   * @param {Array<Object>} data.result.heirs - 相続人リスト
   * @param {string} data.result.heirs[].name - 相続人名
   * @example "配偶者", "子1", "孫2名"
   *
   * @param {string} data.result.heirs[].ratioFraction - 相続割合（分数表記）
   * @example "1/2", "1/4", "1/8"
   *
   * @param {string} data.result.heirs[].ratioPercent - 相続割合（パーセント表記）
   * @example "50%", "25%", "12.5%"
   *
   * @param {string} data.result.heirs[].inheritance - 相続財産額
   * @example "5000万円", "2500万円"
   *
   * @param {string} data.result.heirs[].tax - 相続税額
   * @example "0円", "100万円"
   *
   * @param {string} data.result.totalTax - 相続税総額
   * @example "200万円"
   *
   * @param {boolean} data.result.hasSpouseHeir - 配偶者相続人の有無
   *
   * @return {Promise<object>} APIレスポンス
   * @example
   * // 成功時
   * { success: true }
   *
   * // エラー時
   * { success: false, error: "エラーメッセージ" }
   */
  async send(data) {
    return this.client.post('/send', data);
  }
}
